import type { Root, Text, PhrasingContent } from "mdast";
import type { Plugin } from "unified";
/**
 * Remark plugin that transforms [[artifact_name]] tokens in markdown into
 * custom `artifact-ref` elements, rendered as interactive artifact widgets.
 *
 * The plugin walks text nodes, splits them on the [[...]] pattern, and
 * inserts custom MDAST nodes with `data.hName = "artifact-ref"`. The
 * remark-rehype bridge converts these into `<artifact-ref name="...">` HAST
 * elements, which react-markdown maps to a React component via the
 * `components` prop.
 */
import { visit } from "unist-util-visit";

const ARTIFACT_REF_RE = /\[\[([^\]]+)\]\]/g;

export const remarkArtifactRefs: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, "text", (node: Text, index, parent) => {
      if (index == null || !parent) return;

      const value = node.value;
      if (!ARTIFACT_REF_RE.test(value)) return;

      // Reset regex state after test()
      ARTIFACT_REF_RE.lastIndex = 0;

      const children: PhrasingContent[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = ARTIFACT_REF_RE.exec(value)) !== null) {
        // Text before the match
        if (match.index > lastIndex) {
          children.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }

        // Custom node — data.hName tells remark-rehype which HTML element to emit,
        // data.hProperties become the element's attributes.
        children.push({
          type: "text", // Use text as base type for MDAST compat
          value: "", // Empty — the component renders from props, not children
          data: {
            hName: "artifact-ref",
            hProperties: { name: match[1] },
          },
        } as unknown as PhrasingContent);

        lastIndex = match.index + match[0].length;
      }

      // Trailing text
      if (lastIndex < value.length) {
        children.push({ type: "text", value: value.slice(lastIndex) });
      }

      // Replace the original text node with the split children
      parent.children.splice(index, 1, ...children);
    });
  };
};
